// ScenarioList.tsx - Main listing component with search, filter, and sort capabilities

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Scenario,
  ScenarioFilterOptions,
  ScenarioManagementState,
  GameScenarioContent,
  ScenarioInvestment
} from '../../types/ScenarioManagement';
import { JSONPersistenceService } from '../../services/JSONPersistenceService';
import { investments } from '../../data/investments';
import { incidents } from '../../data/incidents';
import SearchFilter from './SearchFilter';
import ScenarioCard from './ScenarioCard';
import ScenarioTable from './ScenarioTable';
import Pagination from './Pagination';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import EmptyState from './EmptyState';
import { UI_CONSTANTS } from '../../constants';

interface ScenarioListProps {
  onEdit?: (scenario: Scenario) => void;
  onDelete: (scenarioIds: string[]) => void;
  onRestore: (scenarioIds: string[]) => void;
  onExport: (scenarioIds: string[]) => void;
  onCreate: () => void;
  viewMode?: 'grid' | 'table';
  refreshKey?: number;
}

const ScenarioList: React.FC<ScenarioListProps> = ({
  onEdit,
  onDelete,
  onRestore,
  onExport,
  onCreate,
  viewMode = 'grid',
  refreshKey
}) => {
  const navigate = useNavigate();
  const [state, setState] = useState<ScenarioManagementState>({
    scenarios: [],
    selectedScenarios: [],
    filters: {
      sortBy: 'updatedAt',
      sortOrder: 'desc',
      page: 1,
      pageSize: UI_CONSTANTS.DEFAULT_PAGE_SIZE + 2 // 12 items for better grid layout
    },
    isLoading: true,
    error: null,
    totalCount: 0,
    currentPage: 1,
    pageSize: 12
  });

  const [currentViewMode, setCurrentViewMode] = useState(viewMode);
  const [showDeleted, setShowDeleted] = useState(false);

  const handleEditScenario = useCallback((scenario: Scenario) => {
    // Use onEdit callback if provided, otherwise navigate to ScenarioEditor
    if (onEdit) {
      onEdit(scenario);
    } else {
      navigate(`/manage/edit/${scenario.id}`, { state: { scenario } });
    }
  }, [onEdit, navigate]);

  // Load scenarios from JSONPersistenceService
  useEffect(() => {
    loadScenarios();
  }, [state.filters, showDeleted, refreshKey]);

  const loadScenarios = async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      // Get persistence service instance
      const persistence = JSONPersistenceService.getInstance();
      
      // Load scenarios from storage
      let data = await persistence.loadData();
      
      // If no data exists, initialize with default scenarios
      if (!data || !data.scenarios || data.scenarios.length === 0) {
        // Convert game investments to scenario investments
        const scenarioInvestments: ScenarioInvestment[] = investments.map(inv => ({
          id: inv.id,
          name: inv.name,
          icon: inv.icon,
          description: inv.description,
          cost: inv.cost,
          impact: inv.impact,
          businessOutcomes: inv.businessOutcomes || [],
          category: inv.category,
          enabled: true // All investments available by default
        }));
        
        // Convert game incidents to scenario incidents
        const scenarioIncidents = incidents.slice(0, 5).map((inc, index) => ({
          ...inc,
          enabled: true,
          order: index + 1,
          responseOptions: inc.responseOptions.map(opt => ({
            ...opt,
            impact: {
              security: opt.impact.security || 0,
              reputation: opt.impact.reputation || 0,
              financial: opt.impact.financial || 0,
              operational: opt.impact.operational || 0
            }
          }))
        }));
        
        const techCorpContent: GameScenarioContent = {
          // Executive summary for editing
          executiveSummary: `Role:
As Chief Executive Officer of TechCorp International, you lead a Fortune 500 technology company with operations spanning 45 countries and annual revenues exceeding $12 billion. Your leadership has driven consistent growth over the past five years, establishing TechCorp as an industry leader in cloud computing and enterprise software solutions.

Business Context:
The Board of Directors has set ambitious targets for the next fiscal year:

Business Objectives:
• Geographic expansion into emerging markets in Southeast Asia and Latin America
• Integration of advanced AI capabilities across all product lines
• 20% revenue growth while maintaining current profit margins
• Digital transformation of legacy infrastructure systems

Challenge:
Balance aggressive growth objectives with prudent risk management. Every decision you make will impact your company's security posture, financial performance, and stakeholder confidence. The Board expects you to deliver growth while protecting the organization from potentially catastrophic cyber incidents.

This simulation will test your ability to navigate complex risk scenarios, make strategic decisions under pressure, and allocate resources effectively to protect your organization's assets and reputation.`,
          
          // Executive briefing content
          briefing: {
            role: {
              title: 'Your Role',
              description: 'As Chief Executive Officer of TechCorp International, you lead a Fortune 500 technology company with operations spanning 45 countries and annual revenues exceeding $12 billion. Your leadership has driven consistent growth over the past five years, establishing TechCorp as an industry leader in cloud computing and enterprise software solutions.'
            },
            businessContext: {
              title: 'Current Business Context',
              description: 'The Board of Directors has set ambitious targets for the next fiscal year:',
              objectives: [
                'Geographic expansion into emerging markets in Southeast Asia and Latin America',
                'Integration of advanced AI capabilities across all product lines',
                '20% revenue growth while maintaining current profit margins',
                'Digital transformation of legacy infrastructure systems'
              ]
            },
            cybersecurityLandscape: {
              title: 'Cybersecurity Landscape',
              description: 'Recent intelligence reports indicate heightened cyber threat activity targeting technology sector leaders. Your Chief Information Security Officer has briefed you on several concerning developments:',
              threats: [
                {
                  id: 'threat-1',
                  title: 'Nation-State Actors',
                  description: 'Advanced persistent threats targeting intellectual property and strategic plans'
                },
                {
                  id: 'threat-2',
                  title: 'Ransomware Syndicates',
                  description: 'Sophisticated criminal organizations with average demands exceeding $5 million'
                },
                {
                  id: 'threat-3',
                  title: 'Supply Chain Vulnerabilities',
                  description: 'Third-party risks from vendors and partners in your expanding ecosystem'
                },
                {
                  id: 'threat-4',
                  title: 'Insider Threats',
                  description: 'Risks from employee turnover and remote workforce management'
                }
              ]
            },
            challenge: {
              title: 'Your Challenge',
              primaryDescription: 'Balance aggressive growth objectives with prudent risk management. Every decision you make will impact your company\'s security posture, financial performance, and stakeholder confidence. The Board expects you to deliver growth while protecting the organization from potentially catastrophic cyber incidents.',
              secondaryDescription: 'This simulation will test your ability to navigate complex risk scenarios, make strategic decisions under pressure, and allocate resources effectively to protect your organization\'s assets and reputation.'
            }
          },
          objectives: [
            'Successfully expand operations to 3 new geographic regions',
            'Implement AI-powered systems while maintaining security',
            'Achieve regulatory compliance in all operating regions',
            'Maintain customer trust throughout the transformation'
          ],
          successCriteria: [
            { id: '1', description: 'Zero major security breaches during expansion', weight: 30, type: 'required' as const },
            { id: '2', description: 'AI implementation completed within budget', weight: 25, type: 'required' as const },
            { id: '3', description: 'All regional compliance audits passed', weight: 25, type: 'required' as const },
            { id: '4', description: 'Customer satisfaction score above 85%', weight: 20, type: 'optional' as const }
          ],
          availableResources: [
            { id: '1', name: 'Security Budget', type: 'budget' as const, quantity: 5000000 },
            { id: '2', name: 'Technical Team', type: 'personnel' as const, quantity: 50 },
            { id: '3', name: 'Compliance Experts', type: 'personnel' as const, quantity: 10 },
            { id: '4', name: 'AI Infrastructure', type: 'technology' as const, quantity: 1 }
          ],
          constraints: [
            'Expansion must be completed within 18 months',
            'Cannot exceed total budget of $25M',
            'Must maintain existing operations during transition',
            'All AI implementations must be explainable and auditable'
          ],
          timeline: [
            { id: '1', day: 90, title: 'Complete security assessment', description: 'Q1 milestone', impact: 'high' as const },
            { id: '2', day: 180, title: 'Launch in first new region', description: 'Q2 milestone', impact: 'critical' as const },
            { id: '3', day: 270, title: 'AI pilot deployment', description: 'Q3 milestone', impact: 'high' as const },
            { id: '4', day: 365, title: 'Full rollout complete', description: 'Q4 milestone', impact: 'critical' as const }
          ],
          // Game-specific data
          investments: scenarioInvestments,
          incidents: scenarioIncidents,
          gameSettings: {
            totalBudget: 2000000,
            maxInvestmentSelections: 3,
            scoringWeights: {
              security: 30,
              reputation: 25,
              financial: 25,
              operational: 20
            },
            difficultyModifiers: {
              timeLimit: 1.0,
              impactSeverity: 1.0,
              budgetConstraint: 1.0
            }
          }
        };
        
        // M&A Integration scenario content
        const maContent: GameScenarioContent = {
          executiveSummary: `Role:
As Chief Executive Officer of GlobalTech Solutions, you are leading the largest technology sector merger in recent history - a $45 billion acquisition of CloudFirst Systems. This strategic move will position your company as the third-largest enterprise software provider globally, combining your strength in on-premise solutions with CloudFirst's innovative cloud platform.

Business Context:
The merger presents unprecedented opportunities and challenges:

Business Objectives:
• Complete technology stack integration within 18 months
• Achieve $2 billion in cost synergies by year three
• Retain 95% of key technical talent from both organizations
• Maintain zero downtime for critical customer systems during integration

Challenge:
Navigate the complex security landscape of merging two massive technology infrastructures while maintaining operational excellence. You must harmonize different security cultures, integrate disparate systems, and protect against adversaries who see the merger transition as an opportunity to strike.

This simulation will test your ability to manage cybersecurity risks during one of the most vulnerable periods in corporate history - when two giants become one.`,
          
          objectives: [
            'Integrate security systems without compromising either company',
            'Maintain customer trust throughout the merger process',
            'Achieve regulatory approval in all operating jurisdictions',
            'Prevent intellectual property theft during transition'
          ],
          successCriteria: [
            { id: '1', description: 'Zero security breaches during integration', weight: 35, type: 'required' as const },
            { id: '2', description: 'Complete system integration on schedule', weight: 30, type: 'required' as const },
            { id: '3', description: 'Maintain 99.9% uptime for critical systems', weight: 20, type: 'required' as const },
            { id: '4', description: 'Achieve ISO 27001 certification for merged entity', weight: 15, type: 'optional' as const }
          ],
          availableResources: [
            { id: '1', name: 'Integration Budget', type: 'budget' as const, quantity: 8000000 },
            { id: '2', name: 'Security Teams (Combined)', type: 'personnel' as const, quantity: 120 },
            { id: '3', name: 'Integration Specialists', type: 'personnel' as const, quantity: 40 },
            { id: '4', name: 'External Consultants', type: 'personnel' as const, quantity: 20 }
          ],
          constraints: [
            'Integration must be completed within 18 months',
            'Cannot disrupt ongoing customer operations',
            'Must comply with antitrust requirements',
            'Retain both companies\' security certifications'
          ],
          investments: scenarioInvestments.filter(inv => 
            ['zero-trust-architecture', 'data-encryption', 'security-monitoring', 'compliance-program', 'incident-response-team'].includes(inv.id)
          ),
          incidents: scenarioIncidents.slice(0, 6).map((inc, idx) => ({
            ...inc,
            enabled: true,
            order: idx + 1
          })),
          gameSettings: {
            totalBudget: 3000000,
            maxInvestmentSelections: 4,
            scoringWeights: {
              security: 35,
              reputation: 20,
              financial: 25,
              operational: 20
            },
            difficultyModifiers: {
              timeLimit: 0.8,
              impactSeverity: 1.2,
              budgetConstraint: 1.1
            }
          }
        };
        
        // AI Transformation scenario content
        const aiContent: GameScenarioContent = {
          executiveSummary: `Role:
As Chief Executive Officer of FutureTech Industries, you are spearheading an ambitious AI-first transformation that will revolutionize your $20 billion manufacturing and logistics conglomerate. Your vision: to become the world's first fully AI-integrated industrial company, with autonomous systems managing everything from supply chains to quality control.

Business Context:
The AI revolution presents both tremendous opportunities and existential risks:

Business Objectives:
• Deploy AI across all critical business functions within 24 months
• Achieve 40% operational efficiency improvement through automation
• Develop proprietary AI models for competitive advantage
• Maintain ethical AI practices and regulatory compliance

Challenge:
Lead your organization through uncharted territory where AI systems control critical infrastructure and make autonomous decisions affecting millions. You must balance innovation with security, ensuring that your AI transformation doesn't expose the company to catastrophic cyber risks or ethical breaches.

This simulation will test your ability to secure AI systems, protect training data, prevent model manipulation, and maintain human oversight while pushing the boundaries of what's possible with artificial intelligence.`,
          
          objectives: [
            'Deploy AI systems securely across all business units',
            'Protect proprietary AI models and training data',
            'Ensure AI decision-making transparency and accountability',
            'Maintain ethical AI standards and prevent bias'
          ],
          successCriteria: [
            { id: '1', description: 'No AI model poisoning or manipulation incidents', weight: 30, type: 'required' as const },
            { id: '2', description: 'Achieve AI implementation milestones on schedule', weight: 25, type: 'required' as const },
            { id: '3', description: 'Pass all AI ethics and compliance audits', weight: 25, type: 'required' as const },
            { id: '4', description: 'Maintain competitive advantage through AI innovation', weight: 20, type: 'optional' as const }
          ],
          availableResources: [
            { id: '1', name: 'AI Development Budget', type: 'budget' as const, quantity: 10000000 },
            { id: '2', name: 'AI/ML Engineers', type: 'personnel' as const, quantity: 80 },
            { id: '3', name: 'Security Specialists', type: 'personnel' as const, quantity: 60 },
            { id: '4', name: 'Ethics & Compliance Team', type: 'personnel' as const, quantity: 15 }
          ],
          constraints: [
            'Must comply with emerging AI regulations',
            'Cannot compromise existing system security',
            'Maintain explainable AI for critical decisions',
            'Protect intellectual property and trade secrets'
          ],
          investments: scenarioInvestments.filter(inv => 
            ['ai-threat-detection', 'data-encryption', 'zero-trust-architecture', 'security-monitoring', 'cyber-insurance'].includes(inv.id)
          ),
          incidents: scenarioIncidents.slice(2, 8).map((inc, idx) => ({
            ...inc,
            enabled: true,
            order: idx + 1
          })),
          gameSettings: {
            totalBudget: 4000000,
            maxInvestmentSelections: 5,
            scoringWeights: {
              security: 30,
              reputation: 30,
              financial: 20,
              operational: 20
            },
            difficultyModifiers: {
              timeLimit: 0.7,
              impactSeverity: 1.3,
              budgetConstraint: 0.9
            }
          }
        };
        
        // Rapid Scale-Up scenario content
        const rapidScaleContent: GameScenarioContent = {
          executiveSummary: `Role:
As Chief Executive Officer of RocketScale, the fastest-growing fintech startup in history, you've taken your company from 100 to 10,000 employees in just 18 months. Your revolutionary payment platform processes $100 million daily and has attracted $5 billion in venture funding at a $50 billion valuation.

Business Context:
Hypergrowth brings unique cybersecurity challenges that could destroy everything you've built:

Business Objectives:
• Scale security infrastructure to match 100x growth in users
• Achieve SOC 2 Type II and PCI DSS compliance within 6 months
• Build a world-class security team from scratch
• Protect customer financial data as transaction volume explodes

Challenge:
Transform from startup security to enterprise-grade protection while maintaining the speed and agility that made you successful. You must build security into your DNA before adversaries exploit the chaos of hypergrowth to cause irreparable damage.

This simulation will test your ability to scale security at startup speed, make smart build-vs-buy decisions, establish security culture in a rapidly growing organization, and protect against threats targeting high-growth companies.`,
          
          objectives: [
            'Build enterprise security from startup foundations',
            'Achieve compliance certifications required for growth',
            'Scale security team from 5 to 150 professionals',
            'Protect exponentially growing customer data'
          ],
          successCriteria: [
            { id: '1', description: 'No data breaches during scaling period', weight: 35, type: 'required' as const },
            { id: '2', description: 'Achieve all required compliance certifications', weight: 30, type: 'required' as const },
            { id: '3', description: 'Maintain <0.01% security incident rate', weight: 20, type: 'required' as const },
            { id: '4', description: 'Build security team to target size', weight: 15, type: 'optional' as const }
          ],
          availableResources: [
            { id: '1', name: 'Security Budget', type: 'budget' as const, quantity: 6000000 },
            { id: '2', name: 'Current Security Team', type: 'personnel' as const, quantity: 5 },
            { id: '3', name: 'Hiring Budget', type: 'budget' as const, quantity: 3000000 },
            { id: '4', name: 'Board Advisory Support', type: 'technology' as const, quantity: 1 }
          ],
          constraints: [
            'Must not slow product development velocity',
            'Limited pool of experienced security talent',
            'Compliance deadlines are non-negotiable',
            'Cannot afford reputation damage as a financial services company'
          ],
          investments: scenarioInvestments.filter(inv => 
            ['compliance-program', 'backup-recovery', 'security-training', 'security-monitoring', 'incident-response-team'].includes(inv.id)
          ),
          incidents: scenarioIncidents.slice(1, 7).map((inc, idx) => ({
            ...inc,
            enabled: true,
            order: idx + 1
          })),
          gameSettings: {
            totalBudget: 2500000,
            maxInvestmentSelections: 4,
            scoringWeights: {
              security: 30,
              reputation: 35,
              financial: 20,
              operational: 15
            },
            difficultyModifiers: {
              timeLimit: 0.8,
              impactSeverity: 1.2,
              budgetConstraint: 1.0
            }
          }
        };
        
        const defaultScenarios: Scenario[] = [
          {
            id: 'techcorp',
            title: 'TechCorp International',
            subtitle: 'Geographic Expansion & AI Adoption',
            description: 'Navigate cybersecurity challenges as CEO of a Fortune 500 technology company expanding globally and adopting AI technologies.',
            icon: '🏢',
            difficulty: 'Intermediate',
            isAvailable: true,
            createdAt: new Date('2024-01-15'),
            updatedAt: new Date('2024-01-20'),
            version: 1,
            tags: ['AI', 'Expansion', 'Technology', 'Geographic', 'Enterprise'],
            category: 'Technology',
            playCount: 245,
            averageScore: 82.5,
            completionRate: 0.89,
            content: techCorpContent,
            isGameScenario: true
          },
          {
            id: 'ma',
            title: 'M&A Integration',
            subtitle: 'Merger & Acquisition Security',
            description: 'Lead the largest technology sector merger in history while managing complex cybersecurity risks during integration.',
            icon: '🤝',
            difficulty: 'Advanced',
            isAvailable: true,
            createdAt: new Date('2024-01-10'),
            updatedAt: new Date('2024-01-18'),
            version: 1,
            tags: ['M&A', 'Integration', 'Business', 'Risk Management', 'Enterprise'],
            category: 'Business',
            playCount: 89,
            averageScore: 75.3,
            completionRate: 0.72,
            content: maContent,
            isGameScenario: true
          },
          {
            id: 'ai-transform',
            title: 'AI Transformation',
            subtitle: 'Artificial Intelligence Revolution',
            description: 'Spearhead an ambitious AI-first transformation while securing critical systems and maintaining ethical standards.',
            icon: '🤖',
            difficulty: 'Expert',
            isAvailable: true,
            createdAt: new Date('2024-01-12'),
            updatedAt: new Date('2024-01-19'),
            version: 1,
            tags: ['AI/ML', 'Digital Transformation', 'Ethics', 'Innovation', 'Security'],
            category: 'Technology',
            playCount: 156,
            averageScore: 71.2,
            completionRate: 0.68,
            content: aiContent,
            isGameScenario: true
          },
          {
            id: 'rapid-scale',
            title: 'Rapid Scale-Up',
            subtitle: 'Hypergrowth Security Scaling',
            description: 'Scale security infrastructure for a fintech startup experiencing explosive growth from 100 to 10,000 employees.',
            icon: '🚀',
            difficulty: 'Advanced',
            isAvailable: true,
            createdAt: new Date('2024-01-14'),
            updatedAt: new Date('2024-01-21'),
            version: 1,
            tags: ['Growth', 'Scaling', 'Infrastructure', 'FinTech', 'Compliance'],
            category: 'Business',
            playCount: 203,
            averageScore: 78.9,
            completionRate: 0.81,
            content: rapidScaleContent,
            isGameScenario: true
          }
        ];
        
        // Save default scenarios
        await persistence.saveData({
          scenarios: defaultScenarios.map(s => [s.id, s as any]),
          templates: [],
          categories: [],
          tags: [],
          dependencies: []
        });
        
        setState(prev => ({
          ...prev,
          scenarios: defaultScenarios,
          totalCount: defaultScenarios.length,
          isLoading: false
        }));
      } else {
        // Convert Map entries back to scenarios
        const scenarios = data.scenarios.map(([id, scenarioData]) => {
          const scenario = scenarioData as any;
          return {
            ...scenario,
            id,
            createdAt: scenario.createdAt ? new Date(scenario.createdAt) : new Date(),
            updatedAt: scenario.updatedAt ? new Date(scenario.updatedAt) : new Date(),
            deletedAt: scenario.deletedAt ? new Date(scenario.deletedAt) : undefined
          } as Scenario;
        });
        
        setState(prev => ({
          ...prev,
          scenarios,
          totalCount: scenarios.length,
          isLoading: false
        }));
      }
    } catch (error) {
      console.error('Failed to load scenarios:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to load scenarios. Please try again.'
      }));
    }
  };

  const handleFilterChange = useCallback((filters: ScenarioFilterOptions) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, ...filters, page: 1 }
    }));
  }, []);

  const handleSort = useCallback((sortBy: string) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        sortBy: sortBy as any,
        sortOrder: prev.filters.sortBy === sortBy && prev.filters.sortOrder === 'asc' ? 'desc' : 'asc'
      }
    }));
  }, []);



  const handlePageChange = useCallback((page: number) => {
    setState(prev => ({
      ...prev,
      filters: { ...prev.filters, page },
      currentPage: page
    }));
  }, []);

  const filteredScenarios = useMemo(() => {
    let filtered = [...state.scenarios];

    // Apply filters
    if (state.filters.search) {
      const searchLower = state.filters.search.toLowerCase();
      filtered = filtered.filter(s => {
        try {
          return (
            s.title?.toLowerCase().includes(searchLower) ||
            s.subtitle?.toLowerCase().includes(searchLower) ||
            s.description?.toLowerCase().includes(searchLower) ||
            s.tags?.some(tag => tag?.toLowerCase().includes(searchLower))
          );
        } catch (error) {
          console.error('Error filtering scenario:', s.id, error);
          return false;
        }
      });
    }

    if (state.filters.difficulty?.length) {
      filtered = filtered.filter(s => s.difficulty && state.filters.difficulty?.includes(s.difficulty));
    }

    if (state.filters.category?.length) {
      filtered = filtered.filter(s => s.category && state.filters.category?.includes(s.category));
    }

    if (state.filters.isAvailable !== undefined) {
      filtered = filtered.filter(s => s.isAvailable === state.filters.isAvailable);
    }

    if (!showDeleted) {
      filtered = filtered.filter(s => !s.deletedAt);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      try {
        const sortBy = state.filters.sortBy || 'updatedAt';
        const order = state.filters.sortOrder === 'asc' ? 1 : -1;

        switch (sortBy) {
          case 'title':
            return ((a.title || '').localeCompare(b.title || '')) * order;
          case 'difficulty':
            const difficultyOrder = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
            const aIndex = a.difficulty ? difficultyOrder.indexOf(a.difficulty) : -1;
            const bIndex = b.difficulty ? difficultyOrder.indexOf(b.difficulty) : -1;
            return (aIndex - bIndex) * order;
          case 'playCount':
            return ((a.playCount || 0) - (b.playCount || 0)) * order;
          case 'createdAt':
          case 'updatedAt':
            const aDate = a[sortBy] ? new Date(a[sortBy]).getTime() : 0;
            const bDate = b[sortBy] ? new Date(b[sortBy]).getTime() : 0;
            return (aDate - bDate) * order;
          default:
            return 0;
        }
      } catch (error) {
        console.error('Error sorting scenarios:', error);
        return 0;
      }
    });

    return filtered;
  }, [state.scenarios, state.filters, showDeleted]);

  const paginatedScenarios = useMemo(() => {
    const start = (state.currentPage - 1) * state.pageSize;
    const end = start + state.pageSize;
    return filteredScenarios.slice(start, end);
  }, [filteredScenarios, state.currentPage, state.pageSize]);

  const totalPages = Math.ceil(filteredScenarios.length / state.pageSize);

  return (
    <div className="scenario-list-container">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-white mb-2">Manage Scenarios</h2>
            <p className="text-slate-300">Browse, search, and organize your cybersecurity scenarios</p>
          </div>
          <button
            onClick={onCreate}
            className="bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 text-white px-6 py-3 rounded-lg font-medium hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 flex items-center gap-2"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
            Create Scenario
          </button>
        </div>

        {/* Search and Filters */}
        <SearchFilter
          onFilterChange={handleFilterChange}
          viewMode={currentViewMode}
          onViewModeChange={setCurrentViewMode}
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
        />


        {/* Content */}
        {state.isLoading ? (
          <LoadingSpinner />
        ) : state.error ? (
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">{state.error}</p>
            <button
              onClick={loadScenarios}
              className="text-professional-blue-400 hover:text-professional-blue-300"
            >
              Try Again
            </button>
          </div>
        ) : filteredScenarios.length === 0 ? (
          <EmptyState
            message={state.filters.search ? 'No scenarios match your search criteria' : 'No scenarios created yet'}
            action={state.filters.search ? 'Clear Filters' : 'Create First Scenario'}
            onAction={state.filters.search ? () => handleFilterChange({}) : onCreate}
          />
        ) : (
          <>
            {currentViewMode === 'grid' ? (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: { staggerChildren: 0.1 }
                  }
                }}
              >
                <AnimatePresence>
                  {paginatedScenarios.map(scenario => (
                    <ScenarioCard
                      key={scenario.id}
                      scenario={scenario}
                      onEdit={() => handleEditScenario(scenario)}
                      onDelete={() => onDelete([scenario.id])}
                    />
                  ))}
                </AnimatePresence>
              </motion.div>
            ) : (
              <ScenarioTable
                scenarios={paginatedScenarios}
                onSort={handleSort}
                sortBy={state.filters.sortBy}
                sortOrder={state.filters.sortOrder}
                onEdit={handleEditScenario}
                onDelete={onDelete}
              />
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={state.currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScenarioList;